import { Fragment, useState, useEffect, useRef } from 'react';
import {
  ArrowUpCircleIcon,
  FaceFrownIcon,
  FaceSmileIcon,
  HandThumbUpIcon,
  HeartIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import AvatarLocation from './AvatarLocation';
import { useUserData } from '../context/UserData';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { parseISO, format, formatDistanceToNow } from 'date-fns';
import DateFormat from './DateFormat';
import DynamicEmoji from './DynamicEmoji';
import ReactionPost from './ReactionPost';
import PostBody from './PostBody';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const PostComment = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const appRoot = (process.env.REACT_APP_ROOT_URL || '').replace(/\/$/, '');
  const user = useUserData();
  const navigate = useNavigate();
  //codex-- track the hidden file input so we can clear it programmatically after remove/submit.
  const attachmentInputRef = useRef(null);

  // For creating a new post
  const [submitObject, setSubmitObject] = useState({ comment: '', reaction: '' });
  //codex-- store the attachment selected for a new comment post.
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // List of existing comments (posts)
  const [comments, setComments] = useState([]);
  // For replying to an existing comment
  const [replyObject, setReplyObject] = useState({ commentId: null, reply: '' });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Constants
  const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
  //codex-- restrict uploads to requested formats (jpg/png/gif/pdf), validating by mime and extension.
  const ALLOWED_ATTACHMENT_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
  const ALLOWED_ATTACHMENT_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'pdf'];
  //codex-- enforce browser-side file picker filtering for supported formats.
  const FILE_INPUT_ACCEPT = '.jpg,.jpeg,.png,.gif,.pdf,image/jpeg,image/png,image/gif,application/pdf';

  useEffect(() => {
    if (!selectedAttachment || !/\.(jpe?g|png|gif)$/i.test(selectedAttachment.name)) {
      setAttachmentPreview('');
      return;
    }

    const previewUrl = URL.createObjectURL(selectedAttachment);
    setAttachmentPreview(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [selectedAttachment]);

  // Fetch existing comments on mount
  useEffect(() => {
    const fetchComments = async () => {
      try {
        const response = await axios.get(`${apiUrl}/post_crud.php?getList=1`, {
          headers: { 'Content-Type': 'application/json' },
        });
        if (response.data) {
          setComments(Array.isArray(response.data) ? response.data : []);
        } else {
          toast.error('No posts found', { position: 'top-center' });
          setComments([]);
        }
      } catch (error) {
        const isNetworkError =
          error?.code === 'ERR_NETWORK' ||
          error?.message === 'Network Error' ||
          !error?.response;

        if (isNetworkError) {
          console.error('Network error caught locally:', error);
          return;
        }

        if (error.response?.status === 401) {
          navigate('/login');
          return;
        }

        console.error('Other error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [apiUrl, navigate]);

  // Handle text area changes for new comment
  const handleChange = (e) => {
    setSubmitObject({ ...submitObject, comment: e.target.value });
  };

  //codex-- reset attachment state and the native file input value so same-file reselection works.
  const clearSelectedAttachment = () => {
    setSelectedAttachment(null);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  //codex-- validate selected file type before allowing attachment on a new comment.
  const handleAttachmentChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      clearSelectedAttachment();
      return;
    }

    const extension = selectedFile.name.split('.').pop()?.toLowerCase() || '';
    const isValidMimeType = ALLOWED_ATTACHMENT_MIME_TYPES.includes(selectedFile.type);
    const isValidExtension = ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension);

    if (!isValidExtension || (selectedFile.type && !isValidMimeType)) {
      toast.error('Invalid file type. Only JPG, PNG, GIF, and PDF are allowed.', {
        position: 'top-center',
      });
      clearSelectedAttachment();
      return;
    }

    if (selectedFile.size > MAX_ATTACHMENT_SIZE) {
      toast.error('Attachments must be 10 MB or smaller.', { position: 'top-center' });
      clearSelectedAttachment();
      return;
    }

    setSelectedAttachment(selectedFile);
  };

  // If you want the user to pick a reaction for the *new* comment, handle that here
  const handleNewReactionSelect = (reactionType) => {
    setSubmitObject({ ...submitObject, reaction: reactionType });
  };

  // Submits a new comment (post)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!submitObject.comment.trim() && !selectedAttachment) {
      toast.error('Add a comment or choose a file before posting.', { position: 'top-center' });
      return;
    }

    setSubmitting(true);
    try {
      let response;

      //codex-- send multipart payload when an attachment is present; keep legacy JSON flow otherwise.
      if (selectedAttachment) {
        const formData = new FormData();
        formData.append('status', 'addComment');
        formData.append('data[comment]', submitObject.comment);
        formData.append('data[reaction]', submitObject.reaction || '');
        formData.append('file', selectedAttachment);

        response = await axios.post(`${apiUrl}/post_crud.php`, formData);
      } else {
        const bodyData = {
          data: submitObject, // e.g. { comment: 'text here', reaction: 'like' }
          status: 'addComment',
        };

        response = await axios.post(`${apiUrl}/post_crud.php`, bodyData, {
          headers: { 'Content-Type': 'application/json' },
        });
      }

      if (response.data.success) {
        // Prepend new comment
        setComments((previous) => [response.data.comment, ...previous]);
        // Reset form
        setSubmitObject({ comment: '', reaction: '' });
        clearSelectedAttachment();
      } else {
        toast.error(response.data.error || 'Failed adding post.', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error('No access to add.', { position: 'top-center' });
      } else {
        toast.error(error.response?.data?.error || 'Failed to upload post. Please try again.', {
          position: 'top-center',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // For handling typed replies on existing comments
  const handleReplyChange = (e, commentId) => {
    setReplyObject({ commentId, reply: e.target.value });
  };

  // Submits a reply to an existing comment
  const handleReplySubmit = async (e, commentId) => {
    e.preventDefault();
    const bodyData = {
      data: { reply: replyObject.reply },
      status: 'addReply',
      post_id: commentId,
    };
    try {
      const response = await axios.post(`${apiUrl}/post_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.success) {
        // Insert new reply at top of existing replies
        setComments(
          comments.map((comment) =>
            comment.id === commentId
              ? {
                  ...comment,
                  reply: [response.data.reply, ...(comment.reply || [])],
                }
              : comment
          )
        );
        // Clear reply input
        setReplyObject({ commentId: null, reply: '' });
      } else {
        toast.error(response.data.error || 'Failed to update.', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Issue updating data.', { position: 'top-center' });

      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error('No access to update.', { position: 'top-center' });
      }
    }
  };

  // Deletes a comment
  const handleDelete = async (commentId) => {
    try {
      const response = await axios.post(
        `${apiUrl}/post_crud.php`,
        {
          data: { post_id: commentId },
          status: 'delete_comment',
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.data.success) {
        toast.success('Comment deleted!', { position: 'top-center' });
        setComments(comments.filter((comment) => comment.id !== commentId));
      } else {
        toast.error(response.data.error || 'Failed to delete comment.', { position: 'top-center' });
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        navigate('/login');
      } else if (error.response && error.response.status === 403) {
        toast.error('No access to delete.', { position: 'top-center' });
      }
    }
  };

  // Handle a reaction click on an existing comment
  const handleReaction = async (commentId, reactionType) => {
    try {
      // 1) Optimistically update local reaction count
      setComments((prev) =>
        prev.map((comment) => {
          if (comment.id === commentId) {
            // If we have an object with reaction counts, e.g. comment.reactions = { like: 2, smile: 1 }
            const oldCount = comment.reactions?.[reactionType] || 0;
            return {
              ...comment,
              reactions: {
                ...comment.reactions,
                [reactionType]: oldCount + 1,
              },
            };
          }
          return comment;
        })
      );

      // 2) POST to the server (status='reaction')
      const bodyData = {
        status: 'reaction',
        data: {
          post_id: commentId,
          reaction_type: reactionType,
        },
      };
      await axios.post(`${apiUrl}/post_crud.php`, bodyData, {
        headers: { 'Content-Type': 'application/json' },
      });
      // If needed, parse response to see if it was toggled, etc.
    } catch (err) {
      console.error('Error sending reaction:', err);
    }
  };

  // If loading is true, you could show a spinner here, or handle error
  if (loading) {
    return <div className="p-4 text-gray-500">Loading...</div>;
  }
  if (error) {
    return <div className="p-4 text-red-500">Error: {error}</div>;
  }

  return (
    <>
      {/* New Post Form */}
      <div className="flex items-start space-x-4 bg-white p-4 border-2 rounded-lg">
        <div className="flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <form onSubmit={handleSubmit}>
            <fieldset disabled={submitting} className="min-w-0">
              <div className="overflow-hidden rounded-lg shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-indigo-600">
                <label htmlFor="comment" className="sr-only">
                  Add your comment
                </label>
                <textarea
                  rows={2}
                  name="comment"
                  id="comment"
                  value={submitObject.comment}
                  onChange={handleChange}
                  className="block w-full resize-none border-0 bg-transparent py-1.5
                             text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm
                             sm:leading-6"
                  placeholder="Add your comment..."
                />
              </div>

              {attachmentPreview && (
                <img
                  src={attachmentPreview}
                  alt="Selected attachment preview"
                  className="mt-3 max-h-64 max-w-full rounded-lg object-contain"
                />
              )}

              {/* Reaction icons + Post button */}
              <div className="flex flex-wrap items-center justify-between gap-2 py-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  {/*codex-- add file picker entry point for comment attachments. */}
                  <label
                    htmlFor="comment_attachment"
                    className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 hover:cursor-pointer"
                  >
                    Add file
                  </label>
                  {/*codex-- limit selectable files to jpg/png/gif/pdf in the browser picker. */}
                  <input
                    ref={attachmentInputRef}
                    id="comment_attachment"
                    type="file"
                    accept={FILE_INPUT_ACCEPT}
                    onChange={handleAttachmentChange}
                    className="sr-only"
                    aria-describedby="comment_attachment_help"
                  />
                  {/*codex-- show selected filename and allow removing it before submit. */}
                  {selectedAttachment && (
                    <div className="flex items-center gap-2 max-w-xs">
                      <span className="truncate text-xs text-gray-500">{selectedAttachment.name}</span>
                      <button
                        type="button"
                        onClick={clearSelectedAttachment}
                        className="text-xs font-semibold text-red-600 hover:text-red-500"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0">
                  <button
                    type="submit"
                    className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm
                               font-semibold text-white shadow-sm hover:bg-indigo-500
                               focus-visible:outline focus-visible:outline-2
                               focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                  >
                    {submitting ? 'Posting...' : 'Post'}
                  </button>
                </div>
              </div>
              <p id="comment_attachment_help" className="text-xs text-gray-500">
                JPG, PNG, GIF, or PDF. Maximum 10 MB.
              </p>
            </fieldset>
          </form>
        </div>
      </div>

      <br />

      {/* List of existing comments */}
      <div>
        {comments.map((comment) => {
          return (
            <div
              key={comment.id}
              className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow mb-4"
            >
              {/* If logged in user is the author, show delete icon */}
              {user?.users?.id === comment.employee_id && (
                <TrashIcon
                  className="h-5 float-end mr-5 mt-5 text-red-600 hover:cursor-pointer hover:text-blue-500"
                  onClick={() => handleDelete(comment.id)}
                />
              )}

              <div className="px-4 py-5 sm:px-6">
                <div className="flex items-center">
                  <AvatarLocation
                    className="h-10 w-10 rounded-full"
                    avatar={comment.avatar}
                  />
                  <div className="pl-2">
                    <p className="text-sm font-semibold text-gray-900">
                      <a
                        href={`/profile/${comment.employee_id}`}
                        className="hover:underline"
                      >
                        {comment.author}
                      </a>
                    </p>
                    {/* <p className="text-sm text-gray-500">
                      {format(parseISO(comment.date), 'MMMM d, yyyy')}
                    </p> */}
                   <p className="text-sm text-gray-500"
                     title={format(parseISO(comment.date), 'PPP p')}>
                     {format(parseISO(comment.date), 'MMMM d, yyyy')} 
                     <span className='block text-xs text-gray-500'>{formatDistanceToNow(parseISO(comment.date), { addSuffix: true })}</span>
                   </p>
                  </div>
                </div>
              </div>

              <div className="sm:p-6 sm:text-gray-500 p-3">
                <PostBody
                  content={comment.content}
                  fileName={comment.file_name}
                  author={comment.author}
                  appRoot={appRoot}
                />

                {/* Replies */}
                {comment.reply && comment.reply.length > 0 && (
                  <div className="mt-3">
                    {comment.reply.map((reply_post) => (
                      <div
                        key={reply_post.id}
                        className="p-4 bg-[#fafafa] rounded-3xl border mb-3"
                      >
                        <div className="flex items-center">
                          <AvatarLocation
                            className="h-7 w-7 rounded-full"
                            avatar={reply_post.avatar}
                          />
                          <div className="pl-2">
                            <p className="text-xs font-semibold text-indigo-700">
                              {reply_post.author}
                            </p>
                            <p className="text-xs text-gray-500">
                              {reply_post.content}
                            </p>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 pl-8 mt-2">
                          {format(parseISO(reply_post.date), 'MMMM d, yyyy')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reaction Section */}
              <ReactionPost comment={comment} handleReaction={handleReaction} />

              {/* Reply input bar */}
              <div className="p-4 flex items-center">
                <AvatarLocation
                  className="h-7 w-7 rounded-full mr-3"
                  avatar={user?.avatar}
                />
                <input
                  type="text"
                  name="reply"
                  id="reply"
                  placeholder="Reply..."
                  value={replyObject.commentId === comment.id ? replyObject.reply : ''}
                  onChange={(e) => handleReplyChange(e, comment.id)}
                  className="text-slate-500 block w-full rounded-full border-0 py-1.5 text-gray-900 
                             shadow-sm ring-1 ring-inset ring-gray-300 
                             placeholder:text-gray-400 focus:ring-2 focus:ring-inset 
                             focus:ring-indigo-600 sm:text-sm sm:leading-6"
                />
                <ArrowUpCircleIcon
                  className="h-8 text-green-600 hover:cursor-pointer hover:text-blue-600 ml-2"
                  onClick={(e) => handleReplySubmit(e, comment.id)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default PostComment;
